/**
 * @file page.tsx
 * @module app/modules/setting/page
 * @description Main Setting page shell for the new Setting module. Renders
 * tab navigation and switches between Configurations, Mobile Apps,
 * Price Import, and Pages sections.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.9
 * @version 1.3.5
 */

import { useState } from 'react';
import { Card, Nav } from 'react-bootstrap';
import ConfigurationsTab from './configurations';
import MobileAppsTab from './mobile-apps-tab';
import PriceImportTab from './price-import-tab';
import StockImportTab from './stock-import-tab';
import PagesTab from './pages-tab';
import ProcurementParamsTab from './procurement-params-tab';
import DataFixesTab from './data-fixes-tab';
import InwardSlaSettings from '../inward/components/inward-sla-settings';
import { useAuth } from '../auth';
import {
  canPerformInwardAction,
  InwardAction,
} from '../inward/utils/inward-access';
import './styles/page.css';

const SETTINGS_TABS = [
  {
    key: 'configurations',
    label: 'Configurations',
  },
  {
    key: 'mobile-apps',
    label: 'Mobile Apps',
  },
  {
    key: 'price-import',
    label: 'Price Import',
  },
  {
    key: 'stock',
    label: 'Stock',
  },
  {
    key: 'pages',
    label: 'Pages',
  },
  {
    key: 'procurement-params',
    label: 'Manage MOQ/DOIH/Tonnage',
  },
  {
    key: 'data-fixes',
    label: 'Data fixes',
  },
  {
    key: 'inward-sla',
    label: 'Inward SLA',
  },
];

/**
 * Main setting page component.
 * @returns {JSX.Element} Setting shell with tabs and tab content.
 */
const SettingsPage = () => {
  const [activeTab, setActiveTab] = useState(SETTINGS_TABS[0].key);
  const { currentUser } = useAuth();
  // Inward SLA tab only for users with Inward access
  const visibleTabs = SETTINGS_TABS.filter(
    (tab) =>
      tab.key !== 'inward-sla' ||
      canPerformInwardAction(currentUser, InwardAction.View)
  );

  /**
   * Resolves tab content for active tab.
   * @returns {JSX.Element} Active tab component.
   */
  const renderTabContent = () => {
    if (activeTab === 'configurations') {
      return <ConfigurationsTab />;
    }

    if (activeTab === 'mobile-apps') {
      return <MobileAppsTab />;
    }

    if (activeTab === 'price-import') {
      return <PriceImportTab />;
    }

    if (activeTab === 'stock') {
      return <StockImportTab />;
    }

    if (activeTab === 'procurement-params') {
      return <ProcurementParamsTab />;
    }

    if (activeTab === 'data-fixes') {
      return <DataFixesTab />;
    }
    if (activeTab === 'pages') {
      return <PagesTab />;
    }
    if (activeTab === 'inward-sla') {
      return <InwardSlaSettings />;
    }

    return <ConfigurationsTab />;
  };

  return (
    <div className="setting-page-wrapper">
      <Card className="setting-shell-card">
        <Card.Body className="setting-shell-body">
          <div className="setting-shell-header">
            <h1 className="fs-22 fw-bolder mb-2">Settings</h1>
            <p className="text-muted fs-14 mb-0">
              Configure and manage platform-level preferences from one place.
            </p>
          </div>

          <div className="setting-shell-content">
            <Nav variant="tabs" className="setting-tabs">
              {visibleTabs.map((tab) => (
                <Nav.Item key={tab.key}>
                  <Nav.Link
                    active={activeTab === tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={
                      tab.key === 'pages'
                        ? 'setting-tab-link release-highlighter--setting-pages-tab'
                        : 'setting-tab-link'
                    }
                  >
                    {tab.label}
                  </Nav.Link>
                </Nav.Item>
              ))}
            </Nav>

            {renderTabContent()}
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default SettingsPage;